import React, { useEffect, useState } from 'react';
import api from '../../services/api';

/**
 * Cómo está este cliente en la red, ahora mismo.
 *
 * Es lo que se mira cuando alguien llama diciendo que no tiene internet, y estaba
 * repartido entre cuatro pestañas: la IP en una, la señal en otra, el equipo en otra, y
 * para saber si su antena estaba bien había que ir a buscarla.
 *
 * El orden es el de las preguntas que uno se hace por teléfono, y arriba del todo va la
 * única que decide qué hacer: ¿es este cliente, o es el AP? Si medio AP está caído no se
 * manda un técnico a una casa, se sube a la torre.
 */

// Los umbrales son los de AirOS: bajo -75 dBm el enlace empieza a perder paquetes.
const calidadSenal = (dbm) =>
  dbm == null ? { texto: 'sin medir', color: 'rgba(255,255,255,0.35)' }
  : dbm >= -65 ? { texto: 'buena',     color: '#34D399' }
  : dbm >= -75 ? { texto: 'aceptable', color: '#FDE047' }
  :              { texto: 'floja',     color: '#F87171' };

const hace = (fecha) => {
  if (!fecha) return 'nunca';
  const min = Math.floor((Date.now() - new Date(fecha).getTime()) / 60000);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
};

export default function SituacionRed({ clienteId }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!clienteId) return;
    setD(null); setError('');
    api.get(`/api/red/cliente/${clienteId}`)
      .then(r => setD(r.data))
      .catch(e => setError(e.response?.data?.error || e.message));
  }, [clienteId]);

  if (error) return <div style={s.error}>No se pudo consultar la red: {error}</div>;
  if (!d) return <div style={s.cargando}>Consultando la red…</div>;

  const c = d.cliente;
  const sen = calidadSenal(d.senal?.senal_dbm);
  const viejo = d.senal?.medido_en &&
    (Date.now() - new Date(d.senal.medido_en)) > 26 * 3600 * 1000;

  /**
   * De qué tecnología es. Se prefiere el equipo que encontró el router a lo escrito en la
   * ficha: el router refleja la instalación de hoy.
   */
  const tec = d.equipo?.tipo === 'onu' ? 'fibra'
            : (d.equipo?.tipo === 'antena' || d.equipo?.tipo === 'cpe') ? 'radio'
            : (c.tecnologia_instalada || null);
  const esFibra = tec === 'fibra';
  const hayMedida = d.senal?.senal_dbm != null;

  /**
   * La señal, la distancia a la antena, la velocidad del enlace y el tiempo conectado salen
   * de las mediciones de AirOS: una ONU de fibra no las reporta nunca. A un cliente de fibra
   * eran cuatro renglones diciendo «sin dato» para siempre, con todo funcionando bien.
   */
  const mostrarRadio = hayMedida || !esFibra;

  // Lo que este panel puede decir hoy: la señal, el usuario del MikroTik y su antena. Si no
  // hay ninguno de los tres no tiene nada que mostrar, y decir por qué vale más que tres
  // renglones grises.
  const seSabeAlgo = hayMedida || c.mikrotik_usuario || c.ap_nombre;

  /**
   * Cuando no hay nada que mostrar, decir POR QUÉ.
   *
   * Antes salían diez renglones grises seguidos y la ficha parecía rota. Son tres
   * situaciones muy distintas y solo una es un problema que haya que ir a arreglar.
   */
  if (!seSabeAlgo) {
    return (
      <div style={s.caja}>
        <div style={s.titulo}>🛰️ Cómo está en la red</div>
        {c.proveedor_red_nombre ? (
          <div style={s.explica}>
            El servicio de este cliente lo presta <b>{c.proveedor_red_nombre}</b>, así que no
            pasa por nuestro router y aquí no hay nada que medir. <b>Está bien así.</b>
          </div>
        ) : c.mikrotik_usuario && c.mikrotik_tipo && c.mikrotik_tipo !== 'ninguno' ? (
          <div style={s.pendiente}>
            Su {c.mikrotik_tipo === 'pppoe' ? 'cuenta' : 'cola'}{' '}
            <b>«{c.mikrotik_usuario}»</b> existe en el MikroTik pero <b>sin dirección IP</b>.
            Hay que ponérsela allá; el sistema no puede inventarla.
          </div>
        ) : (
          <div style={s.pendiente}>
            No tiene usuario ni cola en el MikroTik, así que no se le puede seguir el rastro.
            Corre <b>Red → Equipos → 🔎 Descubrir equipos de clientes</b> y el sistema saca
            del router lo que haya.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={s.caja}>
      {/* Lo primero: si el problema es del AP, todo lo demás sobra */}
      {d.companeros.parece_el_ap && (
        <div style={s.alarma}>
          ⚠ <b>Esto no parece de este cliente.</b> De los {d.companeros.total} que cuelgan
          de <b>{c.ap_nombre}</b>, {d.companeros.sin_reportar} llevan sin reportar más de un
          día. Antes de mandar a nadie a la casa, revisa la antena
          {c.ap_torre ? ` en ${c.ap_torre}` : ''}.
        </div>
      )}

      <div style={s.titulo}>🛰️ Cómo está en la red</div>

      <div style={s.filas}>
        {mostrarRadio && <>
          <Dato l="Señal" v={hayMedida ? `${d.senal.senal_dbm} dBm` : null}
            color={sen.color}
            nota={hayMedida
              ? `${sen.texto} · medida ${hace(d.senal.medido_en)}${viejo ? ' ⚠' : ''}` : null} />

          {/* Una caída lenta no se ve en el número de hoy, y es la que acaba en visita */}
          {d.deterioro_dbm != null && d.deterioro_dbm <= -6 && (
            <div style={s.aviso}>
              📉 Este enlace ha caído <b>{Math.abs(d.deterioro_dbm)} dBm</b> en las últimas
              semanas. Va a seguir empeorando: conviene revisarlo antes de que se caiga.
            </div>
          )}

          <Dato l="Velocidad del enlace"
            v={d.senal?.tx_mbps ? `${d.senal.tx_mbps} / ${d.senal.rx_mbps} Mbps` : null} />
          <Dato l="Distancia a la antena"
            v={d.senal?.distancia_m ? `${(d.senal.distancia_m / 1000).toFixed(1)} km` : null} />
          <Dato l="Lleva conectado" v={d.senal?.tiempo_conectado} />

          <div style={s.separador} />
        </>}

        {/**
          * Aquí NO se repite lo que ya muestra el panel de arriba —la IP, el equipo, la MAC,
          * la caja—. Salían dos veces en la misma pantalla y, peor, «Cuelga de» significaba
          * la CAJA arriba y la ANTENA aquí: el mismo rótulo para dos datos distintos, uno
          * encima del otro.
          *
          * Este panel se queda con lo suyo, que es lo que el otro no sabe: la señal, el
          * usuario del MikroTik y cómo están los vecinos de su antena.
          */}
        <Dato l="Usuario en MikroTik" v={c.mikrotik_usuario} />
        {/* El AP es cosa de radio: un cliente de fibra no cuelga de ninguna antena. */}
        {(c.ap_nombre || !esFibra) && (
          <Dato l="Por el AP" v={c.ap_nombre}
            nota={c.ap_torre ? `en ${c.ap_torre}` : null} color="#7DD3FC" />
        )}
      </div>

      {/* Los demás del mismo AP: si están bien, el problema es de esta casa */}
      {d.companeros.total > 0 && (
        <div style={s.companeros}>
          <b>{d.companeros.total} clientes más</b> cuelgan de {c.ap_nombre}
          {d.companeros.sin_reportar > 0
            ? <span style={{ color: '#FDE047' }}> · {d.companeros.sin_reportar} sin reportar</span>
            : <span style={{ color: '#86EFAC' }}> · todos reportando bien</span>}
          <div style={s.listaCompaneros}>
            {d.companeros.lista.map(v => {
              const q = calidadSenal(v.senal_dbm);
              const mudo = !v.medido_en || (Date.now() - new Date(v.medido_en)) > 26 * 3600 * 1000;
              return (
                <span key={v.id} style={{ ...s.companero, color: mudo ? '#F87171' : q.color }}>
                  {v.nombre}{v.senal_dbm != null && !mudo ? ` ${v.senal_dbm}` : ' ✕'}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Lo que no se sabe se muestra en gris, no se esconde: así se ve qué falta por descubrir
const Dato = ({ l, v, nota, color }) => (
  <div style={s.fila}>
    <span style={s.etiqueta}>{l}</span>
    <span style={{ ...s.valor, color: v ? (color || '#fff') : 'rgba(255,255,255,0.25)' }}>
      {v || 'sin dato'}
      {v && nota && <span style={s.nota}> ({nota})</span>}
    </span>
  </div>
);

const s = {
  caja:      { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '13px 15px', marginBottom: '14px' },
  titulo:    { fontSize: '12.5px', fontWeight: '700', color: '#A5B4FC', marginBottom: '10px' },
  filas:     { display: 'flex', flexDirection: 'column', gap: '5px' },
  fila:      { display: 'flex', gap: '10px', fontSize: '12.5px', alignItems: 'baseline' },
  etiqueta:  { color: 'rgba(255,255,255,0.42)', width: '165px', flexShrink: 0 },
  valor:     { fontFamily: 'monospace', wordBreak: 'break-all' },
  nota:      { fontFamily: 'inherit', color: 'rgba(255,255,255,0.35)', fontSize: '11.5px' },
  separador: { height: '1px', background: 'rgba(255,255,255,0.06)', margin: '6px 0' },
  alarma:    { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.32)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', color: '#FCA5A5', fontSize: '12.5px', lineHeight: '1.6' },
  aviso:     { background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '8px', padding: '8px 11px', margin: '3px 0', color: '#FDE047', fontSize: '12px', lineHeight: '1.55' },
  companeros:{ marginTop: '11px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.07)', fontSize: '12px', color: 'rgba(255,255,255,0.6)' },
  listaCompaneros: { display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: '6px' },
  companero: { fontSize: '11px', fontFamily: 'monospace' },
  // Lo que simplemente no aplica, en gris; lo que hay que ir a arreglar, en naranja.
  explica:   { fontSize: '12.5px', color: 'rgba(255,255,255,0.45)', lineHeight: '1.65' },
  pendiente: { fontSize: '12.5px', color: '#FDBA74', lineHeight: '1.65' },
  cargando:  { padding: '18px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '12.5px' },
  error:     { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '9px', padding: '11px', marginBottom: '12px', color: '#FCA5A5', fontSize: '12.5px' },
};
